import { api } from '../../../lib/api.js';

/**
 * POST /v1/startAttempt  { course_id, element_id }
 * Response: { status: true, code: 200, data: { attempt: { attempt_id, attempt_name, unit_id } } }
 */
export async function startAttempt(courseId, elementId) {
  const res = await api.request({
    url: 'startAttempt',
    method: 'POST',
    data: { course_id: String(courseId), element_id: String(elementId) },
  });
  return res.data;
}

/**
 * GET /v1/fetchattempt/:attemptId
 * Response: { status: true, code: 200, data: { attempt, next_question, answers,
 *   answer_count, question_count, attempt_progress, show_feedback, time_remaining,
 *   element_name, element_description, is_close } }
 */
export async function fetchAttempt(attemptId, { review = false } = {}) {
  const res = await api.request({ url: `fetchattempt/${attemptId}${review ? '/1' : ''}`, method: 'GET' });
  return res.data;
}

/**
 * GET /v1/fetchinstructions/:courseId/:elementId
 * Legacy adaptive ("Personalized For You") elements open this instruction
 * contract before starting. The response may include continue_attempt_id,
 * in which case the client must resume via fetchAttempt instead of creating
 * a fresh startAttempt.
 */
export async function fetchInstructions(courseId, elementId) {
  const res = await api.request({
    url: `fetchinstructions/${courseId}/${elementId}`,
    method: 'GET',
  });
  return res.data;
}

/**
 * POST /v1/submitAnswer
 * Body: { question_id, user_answer, pause_time, attempt_id, is_audio: 'no', skip_domain: 0 }
 * Response: code 200 = next question | code 204 = element complete
 */
export async function submitAnswer(payload) {
  const res = await api.request({ url: 'submitAnswer', method: 'POST', data: payload });
  return res.data;
}

/**
 * POST /v1/questionFeedback
 * Body: { attempt_id, question_id, feedback }
 * `feedback` is the category, optionally pipe-joined with a free-text note
 * (e.g. 'audio|no sound at 0:12'). Legacy reloaded on success; new app
 * shows a toast and closes the modal.
 * Response: { status: true, code: 200, ... }
 */
export async function reportQuestion({ attempt_id, question_id, feedback }) {
  const res = await api.request({
    url: 'questionFeedback',
    method: 'POST',
    data: { attempt_id, question_id, feedback },
  });
  return res.data;
}

/**
 * POST /v1/checkVideo
 * Legacy fallback behind "If the video is not loading, click here".
 * code 204 means reload the question; code 200/other means surface message.
 */
export async function checkVideo({ attempt_id, question_id, videoURL }) {
  const res = await api.request({
    url: 'checkVideo',
    method: 'POST',
    data: { attempt_id, question_id, videoURL },
  });
  return res.data;
}

/**
 * GET /v1/up-next
 * Response: { status: true, code: 200, data: [...] | {...} }
 * Legacy used this on /mydashboard. The new app calls it when the lesson
 * completes and uses the first item to populate CompletionScreen's
 * "what's next" recommendation. Swallowed on error — CompletionScreen
 * falls back to course image + Back-to-course CTA.
 */
export async function fetchUpNext() {
  const res = await api.request({ url: 'up-next', method: 'GET' });
  return res.data;
}
